import { useState } from 'react';

import { ChevronRight } from 'lucide-react';

import { LaneReasoning } from 'types/agents/QuickQuoteAgent';
import { cn } from 'utils/shadcn';

import {
  LaneReasoningDetails,
  OpenInQuickQuoteHandler,
} from './LaneReasoningDetails';
import { OutcomeChip } from './OutcomeChip';
import { formatDollars, splitLaneLabel } from './reasoningHelpers';

type BatchLaneListProps = {
  lanes: LaneReasoning[];
  onOpenInQuickQuote: OpenInQuickQuoteHandler;
};

const laneKey = (lane: LaneReasoning, index: number) =>
  `${lane.quoteRequestId ?? 'lane'}-${index}`;

// One compact row per lane; each row expands into that lane's full card.
export function BatchLaneList({
  lanes,
  onOpenInQuickQuote,
}: BatchLaneListProps) {
  const [openLaneKey, setOpenLaneKey] = useState<string | null>(null);

  return (
    <ul
      className='w-full divide-y divide-neutral-200 rounded-[4px] border border-neutral-200 bg-neutral-50'
      data-testid='agent-batch-lanes'
    >
      {lanes.map((lane, index) => {
        const key = laneKey(lane, index);
        const isOpen = openLaneKey === key;
        const panelId = `agent-lane-panel-${key}`;
        const label = splitLaneLabel(lane.laneLabel);

        return (
          <li key={key} className='w-full'>
            <button
              type='button'
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenLaneKey(isOpen ? null : key)}
              className={cn(
                'flex w-full cursor-pointer items-start gap-1.5 px-2 py-2 text-left hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
                isOpen && 'bg-neutral-100'
              )}
            >
              <ChevronRight
                className={cn(
                  'mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-500 transition-transform',
                  isOpen && 'rotate-90'
                )}
                aria-hidden
              />
              {/* Route gets the full row and wraps; status and reference sit underneath. */}
              <span className='flex min-w-0 flex-1 flex-col gap-1'>
                <span
                  className='break-words text-[12px] font-medium leading-snug text-neutral-900'
                  title={lane.laneLabel}
                >
                  {label.lane}
                </span>
                <span className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
                  <OutcomeChip outcome={lane.outcome} />
                  {label.reference && (
                    <span className='text-[10.5px] text-neutral-500 tabular-nums'>
                      {label.reference}
                    </span>
                  )}
                </span>
              </span>
              <span className='shrink-0 pl-1 text-right text-[12.5px] font-semibold leading-snug text-neutral-900 tabular-nums'>
                {lane.sellRate !== undefined
                  ? formatDollars(lane.sellRate)
                  : '—'}
              </span>
            </button>
            {isOpen && (
              <div id={panelId} className='w-full px-2.5 pb-2.5 pt-1'>
                <LaneReasoningDetails
                  lane={lane}
                  onOpenInQuickQuote={onOpenInQuickQuote}
                  isBatchLane
                />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
